import { FormEvent, useState } from 'react';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon,
  IonInput, IonLoading, IonPage, IonTitle, IonToolbar,
} from '@ionic/react';
import { checkmarkCircleOutline, mailOutline } from 'ionicons/icons';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import { getAuthRetryAfter, recordAuthAttempt } from '../../utils/AuthRateLimit';
import '../home/AuthForms.css';
import './PWRecovery.css';

const RESET_WINDOW = 60_000;

const PWRecovery: React.FC = () => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sent, setSent] = useState(false);

  const handleReset = async (event: FormEvent) => {
    event.preventDefault();
    if (website) return;

    const retryAfter = getAuthRetryAfter('password-reset', 3, RESET_WINDOW);
    if (retryAfter) {
      setErrorMessage(`Too many requests. Please wait ${retryAfter} seconds and try again.`);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      recordAuthAttempt('password-reset', RESET_WINDOW);
      await sendPasswordResetEmail(auth, email.trim());
      setSent(true);
    } catch (error: any) {
      if (error?.code === 'auth/invalid-email') {
        setErrorMessage('Please enter a valid email address.');
      } else if (error?.code === 'auth/too-many-requests') {
        setErrorMessage('Too many requests. Please wait a little before trying again.');
      } else {
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage className="recovery-page">
      <IonHeader className="recovery-header">
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/" text="Back" /></IonButtons>
          <IonTitle>Password recovery</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="recovery-content">
        <main className="recovery-shell">
          <section className="recovery-card">
            <div className="recovery-mascot" aria-hidden="true">
              <span />
              <img src="/assets/ploppy/ploppy-advisor-reading-noglasses.png" alt="" />
            </div>

            {sent ? (
              <div className="recovery-success" role="status">
                <IonIcon icon={checkmarkCircleOutline} />
                <span>Check your inbox</span>
                <h1>Reset link requested</h1>
                <p>If an account exists for <strong>{email.trim()}</strong>, Firebase will send a password reset link. Please also check your spam folder.</p>
                <IonButton routerLink="/" expand="block" shape="round">Back to PawerLifting</IonButton>
                <button type="button" className="auth-text-action" onClick={() => setSent(false)}>Use another email</button>
              </div>
            ) : (
              <form className="auth-form recovery-form" onSubmit={handleReset}>
                <div className="auth-form__intro">
                  <span>Forgot your password?</span>
                  <h1>Let’s get you back in.</h1>
                  <p>Enter the email connected to your PawerLifting account. We’ll ask Firebase to send you a secure reset link.</p>
                </div>
                <input className="auth-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={event => setWebsite(event.target.value)} />
                <IonInput className="auth-input" label="Email" labelPlacement="stacked" fill="outline" type="email" autocomplete="email" inputmode="email" value={email} onIonInput={event => setEmail(event.detail.value || '')} required>
                  <IonIcon icon={mailOutline} slot="start" aria-hidden="true" />
                </IonInput>
                {errorMessage && <p className="auth-error" role="alert">{errorMessage}</p>}
                <IonButton type="submit" expand="block" shape="round" disabled={loading || !email.trim()}>Send reset link</IonButton>
                <p className="recovery-note">For your privacy, we do not confirm whether an email is registered.</p>
              </form>
            )}
          </section>
        </main>
        <IonLoading isOpen={loading} message="Requesting your reset link…" />
      </IonContent>
    </IonPage>
  );
};

export default PWRecovery;
